/**
 * <PermissionPromptCoach> — pendant que le navigateur pose sa question d'accès (molecule, `proposé`).
 *
 * Le voile de WhatsApp Web : l'écran s'assombrit, une carte dit quoi choisir et pour qui, et une flèche
 * pointe vers l'endroit où le navigateur a ouvert sa question — en haut à gauche (`top-left` : Chrome,
 * Edge, Firefox), en haut au milieu (`top-center` : Safari sur Mac). Sans flèche (`null`), la question
 * est au milieu de l'écran (un téléphone, une tablette) : la carte descend en bas, pour ne pas la cacher.
 *
 * Le live s'efface plus que sous une boîte (le voile redoublé, `<Overlay scrim="deep">`) : ce qui compte
 * est hors de la page, dans la fenêtre du navigateur. La page ne la dessine pas : la flèche pointe vers le
 * bord de la page, au plus près. Elle avance et recule doucement vers la question, sans rebond ; en mouvement réduit, elle reste
 * posée. L'entrée (le voile en fondu, la carte) est celle de toutes les boîtes (`<Overlay>`).
 *
 * Rien ne se ferme au toucher du voile : un clic à côté replie la question de certains navigateurs sans y
 * répondre. « OK » ferme la carte (`onDismiss`) ; la réponse de l'élève la retire d'elle-même.
 *
 * La carte est celle de `<ConfirmDialog>` (sa largeur, sa surface, ses boutons à droite) ; la flèche est
 * sa sœur, hors de la carte.
 *
 * Composes `<Overlay>` + `<Icon>` + `<Title>` / `<Text>` + `<Row>` + `<Button>`.
 */
import React, { useEffect, useMemo, useRef } from "react"
import { Animated, Easing, Platform, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Overlay } from "../../atoms/Overlay"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { useReduceMotion } from "../../hooks/useReduceMotion"

export type PermissionPromptArrow = "top-left" | "top-center" | null

export type PermissionPromptCoachProps = {
    visible: boolean
    title: string
    message: string
    /** Là où la question du navigateur s'ouvre ; `null` : au milieu de l'écran, pas de flèche. */
    arrow: PermissionPromptArrow
    okLabel: string
    onDismiss: () => void
    testID?: string
}

/** La flèche : grande, lisible sur le voile, sans être un bouton. */
const ARROW_SIZE = 80
/** L'aller-retour de la flèche vers la question, en px. */
const NUDGE = 8

export function PermissionPromptCoach(props: PermissionPromptCoachProps) {
    const { visible, title, message, arrow, okLabel, onDismiss, testID } = props
    const reduceMotion = useReduceMotion()
    const { motion } = useUnistyles().theme
    const insets = useSafeAreaInsets()
    const nudge = useRef(new Animated.Value(0)).current

    useEffect(() => {
        if (!visible || arrow == null || reduceMotion) {
            nudge.setValue(0)
            return undefined
        }
        const easing = Easing.bezier(...motion.easing)
        const useNativeDriver = Platform.OS !== "web"
        const loop = Animated.loop(
            Animated.sequence([
                Animated.timing(nudge, { toValue: 1, duration: motion.entree, easing, useNativeDriver }),
                Animated.timing(nudge, { toValue: 0, duration: motion.entree, easing, useNativeDriver }),
            ])
        )
        loop.start()
        return () => loop.stop()
    }, [visible, arrow, reduceMotion, nudge, motion])

    const shift = useMemo(() => nudge.interpolate({ inputRange: [0, 1], outputRange: [0, -NUDGE] }), [nudge])
    // Vers le haut à gauche : la flèche (montante) tourne d'un huitième de tour, et avance en diagonale.
    const diagonal = arrow === "top-left"

    return (
        <Overlay
            visible={visible}
            onRequestClose={onDismiss}
            dismissable={false}
            scrim="deep"
            placement={arrow == null ? "bottom" : "center"}
            testID={testID}
        >
            {arrow != null && (
                <Animated.View
                    style={[
                        styles.arrow(arrow),
                        {
                            transform: [
                                { translateX: diagonal ? shift : 0 },
                                { translateY: shift },
                                { rotate: diagonal ? "-45deg" : "0deg" },
                            ],
                        },
                    ]}
                    pointerEvents="none"
                    testID={testID != null ? `${testID}-arrow-${arrow}` : undefined}
                >
                    <Icon name="arrow-up" size={ARROW_SIZE} color="accent" />
                </Animated.View>
            )}
            <View style={styles.card(arrow == null ? insets.bottom : null)} accessibilityRole="alert">
                <Stack gap="4">
                    <Stack gap="2">
                        <Title size="text-lg">{title}</Title>
                        <Text color="text-muted">{message}</Text>
                    </Stack>
                    <Row justify="end">
                        <Button label={okLabel} onPress={onDismiss} />
                    </Row>
                </Stack>
            </View>
        </Overlay>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Au milieu : une bande de la largeur de l'écran, qui centre la flèche. */
    arrow: (arrow: "top-left" | "top-center") => ({
        position: "absolute" as const,
        top: theme.spacing["4"],
        ...(arrow === "top-left" ? { left: theme.spacing["6"] } : { left: 0, right: 0, alignItems: "center" as const }),
    }),
    /** `bottom` : posée en bas (au téléphone), au-dessus de la zone sûre ; `null` : au milieu. */
    card: (bottom: number | null) => ({
        width: 320,
        maxWidth: "90%" as const,
        backgroundColor: theme.colors["surface-raised"],
        borderRadius: theme.radius.lg,
        padding: theme.spacing["5"],
        marginBottom: bottom != null ? bottom + theme.spacing["6"] : 0,
    }),
}))
